/* eslint-disable jsx-a11y/media-has-caption */
import { useEffect, useRef, useState } from "react";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import classes from "./VideoPlayer.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import getImagePath from "@Utils/getImagePath";
import PlayIcon from "@Icons/PlayIcon";
import Pause from "@Icons/Pause";
import useIsDesktop from "hooks/useIsDesktop";

type VideoPlyrProps = {
  src: string;
  thumbnailUrl: string;
  isYoutube?: boolean;
  controls?: boolean;
  height?: string;
  className?: string;
  autoPlay?: boolean;
  fromGalleryCarousel?: boolean;
};

const Video = ({
  src = "",
  thumbnailUrl = "",
  controls = true,
  className = "",
  autoPlay = false,
  fromGalleryCarousel = false,
}) => {
  const [poster, setPoster] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const timeoutRef = useRef<Array<NodeJS.Timeout>>([]);
  const isMountedRef = useRef(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasAutoPlayedRef = useRef(false);
  const fallbackThumbnailUrl = useRef(
    getImagePath(
      "https://s3.ap-southeast-1.amazonaws.com/static-assets.amberstudent.com/images/video_thumbnail.png",
    ),
  );
  const hideButtonTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isDesktop = useIsDesktop();

  const isVideoPlaying = (video: any) =>
    !!(video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2);

  useEffect(() => {
    isMountedRef.current = true;
    let attempts = 0;

    const loadImage = () => {
      const img = new Image();
      img.src = thumbnailUrl;

      img.onload = () => {
        if (isMountedRef.current) setPoster(thumbnailUrl);
      };

      img.onerror = () => {
        attempts += 1;
        if (attempts < 12) {
          const timeoutId = setTimeout(loadImage, 800);
          timeoutRef.current.push(timeoutId);
        } else if (isMountedRef.current) {
          setPoster(fallbackThumbnailUrl.current);
        }
      };
    };

    loadImage();

    return () => {
      isMountedRef.current = false;
      timeoutRef.current.forEach(clearTimeout);
      timeoutRef.current = [];
    };
  }, [thumbnailUrl, src]);

  useEffect(() => {
    // Reset autoplay state when src changes (new video)
    hasAutoPlayedRef.current = false;
    setIsPlaying(false);
  }, [src]);

  useEffect(() => {
    if (videoRef.current && autoPlay && !hasAutoPlayedRef.current) {
      // Check if the video element is actually visible in the viewport
      const rect = videoRef.current.getBoundingClientRect();
      const isVisible =
        rect.width > 0 &&
        rect.height > 0 &&
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.right <= window.innerWidth;

      if (isVisible && !isVideoPlaying(videoRef.current)) {
        hasAutoPlayedRef.current = true;
        videoRef.current.play().catch(() => {
          hasAutoPlayedRef.current = false;
        });
      }
    }
  }, [autoPlay]);
  useEffect(
    () => () => {
      if (hideButtonTimeoutRef.current) {
        clearTimeout(hideButtonTimeoutRef.current);
      }
    },
    [],
  );

  // Add effect to pause videos that are no longer visible
  useEffect(() => {
    if (!autoPlay && videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
  }, [autoPlay]);

  const handlePlay = () => {
    setIsPlaying(true);
    setShowButton(true);

    // Start auto-hide timer when video plays
    if (hideButtonTimeoutRef.current) {
      clearTimeout(hideButtonTimeoutRef.current);
    }
    hideButtonTimeoutRef.current = setTimeout(() => {
      setShowButton(false);
    }, 3000);

    gtagEventEmitter({
      action: "video_played",
      category: "inventory_page",
    });
  };

  const handlePause = () => {
    setIsPlaying(false);
    setShowButton(true); // Keep button visible when paused

    // Clear auto-hide timer when paused
    if (hideButtonTimeoutRef.current) {
      clearTimeout(hideButtonTimeoutRef.current);
    }
  };

  const handlePlayPauseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowButton(true); // Show button on click

    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const handleMouseActivity = () => {
    setShowButton(true);

    // Only set auto-hide timer if video is playing
    if (isPlaying) {
      // Clear existing timeout
      if (hideButtonTimeoutRef.current) {
        clearTimeout(hideButtonTimeoutRef.current);
      }

      // Set new timeout to hide button after 3 seconds of inactivity
      hideButtonTimeoutRef.current = setTimeout(() => {
        setShowButton(false);
      }, 3000);
    }
  };

  const handleMouseLeave = () => {
    // Only hide on mouse leave if video is playing
    if (isPlaying) {
      if (hideButtonTimeoutRef.current) {
        clearTimeout(hideButtonTimeoutRef.current);
      }
      setShowButton(false);
    }
  };

  return (
    <>
      {fromGalleryCarousel && isDesktop ? (
        <div
          className={classes.videoContainer}
          role="none"
          onMouseMove={handleMouseActivity}
          onMouseEnter={handleMouseActivity}
          onMouseLeave={handleMouseLeave}
        >
          <video
            className={formatClassNames(classes.video, className)}
            playsInline
            controls={controls}
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            poster={poster || fallbackThumbnailUrl.current}
            preload="none"
            ref={videoRef}
            onPlay={handlePlay}
            onPause={handlePause}
          >
            <source className={classes.video} src={src} />
          </video>
          {fromGalleryCarousel && showButton && (
            <div
              role="none"
              onClick={handlePlayPauseClick}
              className={classes.videoClassPlayButton}
            >
              {isPlaying ? (
                <Pause width="28" height="28" fill="white" />
              ) : (
                <PlayIcon width="28" height="28" />
              )}
            </div>
          )}
        </div>
      ) : (
        <video
          className={formatClassNames(classes.video, className)}
          playsInline
          controls={controls}
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          poster={poster || fallbackThumbnailUrl.current}
          preload="none"
          ref={videoRef}
          onPlay={handlePlay}
        >
          <source className={classes.video} src={src} />
        </video>
      )}
    </>
  );
};

const VideoPlayer = ({
  src,
  thumbnailUrl,
  isYoutube = false,
  controls = true,
  height,
  className,
  autoPlay = false,
  fromGalleryCarousel = false,
}: VideoPlyrProps) =>
  isYoutube ? (
    <iframe
      title="youtube-video"
      src={src}
      width="100%"
      height={height || "100%"}
      sandbox="allow-scripts allow-same-origin"
    />
  ) : (
    <Video
      className={className}
      controls={controls}
      thumbnailUrl={thumbnailUrl}
      src={src}
      autoPlay={autoPlay}
      fromGalleryCarousel={fromGalleryCarousel}
    />
  );

export default wrapperHOC(VideoPlayer, {
  componentName: "VideoPlayer-GalleryModal-PropertyPage",
  showForChina: true,
});
